import styled from '@emotion/styled';
import { ResizeObserver } from '@juggle/resize-observer';
import {
  device,
  ExpandPlus,
  sronly,
  typography,
  useMedia,
  useRevealTrail,
} from '@landsbankinn/ui';
import React, { useState } from 'react';
import { animated, useSpring } from 'react-spring';
import useMeasure from 'react-use-measure';

interface IProps {
  children: React.ReactNode;
  columns?: number;
  isCollapsed?: boolean;
  title: string | null;
  hasImage: boolean;
}

const Host = styled(animated.div)<any>`
  height: ${(props) => (props['data-collapsed'] ? '540px' : 'auto')};
  overflow: ${(props) => (props['data-collapsed'] ? 'hidden' : 'vidible')};
`;

const Title = styled.h2`
  ${typography.h2}

  margin-bottom: 40px;
  margin-top: 40px;

  @media ${device.tablet} {
    margin-bottom: 80px;
  }
`;

const CardGroup = styled.div<{ columns: number }>`
  flex-direction: column;

  > * {
    margin-left: 0;
    margin-bottom: 20px;
    max-width: 100%;
  }

  @media ${device.tablet} {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    max-width: 1200px;
    margin: 0 -10px;

    > * {
      margin: 0 10px;
      margin-bottom: 20px;
      width: 100%;
      max-width: calc(${(props) => (1 / props.columns) * 100}% - 20px);
    }
  }
`;

const ExpandBtn = styled(animated.button)<{ isOpen: boolean }>`
  position: absolute;
  bottom: 0;
  right: 1px;
  left: 1px;

  height: ${(props) => (props.isOpen ? '100px' : '200px')};
  width: calc(100% - 2px);
  overflow: visible;

  cursor: pointer;

  background-image: linear-gradient(
    0deg,
    rgba(244, 247, 247, 1) 0%,
    rgba(244, 247, 247, 1) 50%,
    rgba(244, 247, 247, 0) 100%
  );

  > div {
    position: absolute;
    left: calc(50% - 11px);
    bottom: -20px;
  }
`;

const SrOnly = styled.span`
  ${sronly}
`;

export const MoreInfoCardGroup = ({
  children,
  columns = 3,
  isCollapsed,
  title,
  hasImage,
}: IProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [copyRef, copySize] = useMeasure({ polyfill: ResizeObserver });
  const isMobile = useMedia('md');

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const setHeight = useSpring({
    height:
      isOpen && isCollapsed && !isMobile
        ? `${copySize?.height + 160}px`
        : !isCollapsed || isMobile
        ? 'auto'
        : hasImage
        ? '800px'
        : '700px',
  });

  const fadeOut = useSpring({
    backgroundImage: isOpen
      ? `linear-gradient(
      0deg,
      rgba(244, 247, 247, 0) 0%,
      rgba(244, 247, 247, 0) 50%,
      rgba(244, 247, 247, 0) 100%
    )`
      : `linear-gradient(
        0deg,
        rgba(244, 247, 247, 1) 0%,
        rgba(244, 247, 247, 1) 50%,
        rgba(244, 247, 247, 0) 100%
    )`,
  });

  const { ref, items } = useRevealTrail(children, { delay: 300 });

  return (
    <Host
      ref={ref}
      style={{ ...setHeight } as any}
      data-collapsed={isCollapsed}
    >
      <Title>{title}</Title>
      <CardGroup ref={copyRef} columns={columns}>
        {items}
      </CardGroup>
      {!!isCollapsed && !isMobile && (
        <ExpandBtn
          onClick={toggle}
          role="button"
          aria-expanded={isOpen ? true : false}
          style={{ ...fadeOut } as any}
          isOpen={isOpen}
        >
          <ExpandPlus size="medium" theme="secondary" isOpen={isOpen} />
          <SrOnly>See more</SrOnly>
        </ExpandBtn>
      )}
    </Host>
  );
};
